import { HttpError, NetworkError } from '@rover/rsdk/src/errors';

import { captureException } from './sentry';

/** The shape `APIError` (`@rover/utilities/APIError`) presents. Duck-typed rather
 * than imported so this module stays free of a `@rover/utilities` dependency. */
type ErrorWithStatus = { status: number };

const hasNumericStatus = (error: unknown): error is ErrorWithStatus =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as ErrorWithStatus).status === 'number';

/** `ErrorReporting/types.ts` defines `SilentError { silent: true }` and
 * `captureError` honours it. We call `captureException` directly (see below), so
 * we have to honour it ourselves. */
const isSilentError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { silent?: unknown }).silent === true;

/**
 * An axios failure that never got a response: offline, DNS, timeout, or a
 * cancel. There is no status to judge and nothing went wrong on our side.
 *
 * Needed because the legacy duck path doesn't hand us a `NetworkError`.
 * `DefaultAPIErrorHandler` only builds an `APIError` when a response exists
 * (`if (!error.response) throw error`) — everything else is rethrown as the raw
 * `AxiosError`, which reaches us with `name: 'AxiosError'` and no status, and
 * would otherwise be reported as `Unexpected AxiosError from phones.addPhone`
 * every time someone's train went into a tunnel. Cancels arrive the same way
 * (`CanceledError` also sets `isAxiosError`) and are equally routine —
 * react-query cancels in-flight queries as a matter of course.
 *
 * Duck-typed on the `isAxiosError` flag — which is exactly what
 * `axios.isAxiosError` checks — so this module keeps its axios-free import
 * graph, the same reason `APIError` is duck-typed above.
 */
const isResponselessAxiosError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { isAxiosError?: unknown }).isAxiosError === true &&
  (error as { response?: unknown }).response == null;

/**
 * Pulls an HTTP status off the error shapes our two data layers throw:
 * `HttpError` from the generated rsdk client, `APIError` from the legacy
 * `*WithErrorHandling` + `DefaultAPIErrorHandler` path.
 *
 * Returns `undefined` for anything else — a `NetworkError`, or a plain `throw`
 * from our own code. Those are not HTTP failures and are handled separately.
 */
export const getStatusFromError = (error: unknown): number | undefined => {
  if (error instanceof HttpError) return error.statusCode;
  if (error instanceof NetworkError) return undefined;
  if (hasNumericStatus(error)) return error.status;
  return undefined;
};

/** Drops the query string and fragment, which can carry ids and, occasionally,
 * PII, before the url goes to Sentry as context. */
const stripUrlQuery = (url: string): string => url.split(/[?#]/)[0];

/** Method and URL, when the error carries the axios response that produced it. */
const getRequestInfo = (error: unknown): { method?: string; url?: string } => {
  if (!(error instanceof HttpError)) return {};

  const config = error.rawResponse?.config;
  if (!config) return {};

  return {
    method: config.method?.toUpperCase(),
    ...(config.url && { url: stripUrlQuery(config.url) }),
  };
};

/** Low-cardinality label for a throw with no HTTP status, so two different
 * non-HTTP bugs in the same operation don't share a Sentry issue. */
const getErrorLabel = (error: unknown): string => {
  if (error instanceof Error) return error.name || 'Error';
  return 'non-Error throw';
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return String(error);
};

export type ReportUnexpectedApiErrorArgs = {
  /**
   * Stable, human-readable name for the API operation, e.g. `phones.addPhone`.
   * Goes into the reported message and therefore into the Sentry fingerprint —
   * keep it stable and free of ids.
   */
  operation: string;
  error: unknown;
  /**
   * Statuses that are a designed part of this operation's API contract and are
   * already surfaced to the user. These are not reported. Anything else is.
   */
  expectedStatuses: readonly number[];
};

/** The decision and the report itself. Wrapped by the exported function below,
 * which is the half that guarantees none of this reaches the caller. */
const report = ({ operation, error, expectedStatuses }: ReportUnexpectedApiErrorArgs): void => {
  if (isSilentError(error)) return;

  // Offline and timeout noise. Deliberately not reported — the same noise
  // `orvalHTTPInterface`'s `Logger.info` downgrade exists to suppress.
  if (error instanceof NetworkError) return;

  // The same noise, arriving down the legacy duck path as a raw AxiosError
  // rather than a NetworkError. See `isResponselessAxiosError`.
  if (isResponselessAxiosError(error)) return;

  const status = getStatusFromError(error);

  if (status !== undefined && expectedStatuses.includes(status)) return;

  const message =
    status !== undefined
      ? `Unexpected ${status} from ${operation}`
      : `Unexpected ${getErrorLabel(error)} from ${operation}`;

  const reportedError = new Error(message);
  reportedError.name = 'UnexpectedApiError';

  const { method, url } = getRequestInfo(error);

  captureException(reportedError, {
    errorInfo: {
      operation,
      ...(status !== undefined && { statusCode: status }),
      ...(method && { method }),
      ...(url && { url }),
      originalMessage: getErrorMessage(error),
      ...(error instanceof Error && error.stack && { originalStack: error.stack }),
    },
  });
};

/**
 * Reports an API failure that isn't part of an operation's contract to Sentry.
 *
 * Call this from the data layer, from inside the `catch` that already handles the
 * failure — that layer is the only one that knows which statuses a given
 * operation expects. Errors are still thrown and handled exactly as before; this
 * only adds visibility.
 *
 * Deliberately per-operation rather than a blanket interceptor over every HTTP
 * request. Each call site opts in with a status table it can justify, so the
 * volume it can produce is knowable before it ships, and a status that is a
 * designed answer on one endpoint is not treated as a failure on another. Adding
 * a call site is small: see `react-lib/src/apis/reportPhoneApiError` for the
 * pattern — a thin wrapper holding one API area's expected-status table, called
 * from that area's existing `catch` blocks.
 *
 * Four deliberate choices, all of which a naive implementation gets wrong:
 *
 * - **A fresh `Error`, not the caught one.** `sentry.ts` fingerprints on
 *   `error.message`, and `HttpError`'s message is only `HTTP Error <status>`, so
 *   reporting the caught error raw would collapse every failure in the app into a
 *   single Sentry issue. The message here carries operation + status and no url,
 *   so one broken operation is one issue however many records it was called for.
 * - **`captureException`, not `Logger.error`.** `Logger.error` forwards metadata
 *   to Splunk but calls `captureError(error)` with no context, so status and URL
 *   never reach Sentry.
 * - **`captureException`, not `captureError`.** `captureError` re-throws in local
 *   dev (`if (isDevNodeEnv()) throw errorObject`). Called from inside a `catch`
 *   that would escape the catch and change local control flow.
 * - **Nothing escapes.** Every caller runs this from inside a `catch`, before it
 *   sets its own error state, so a throw from here — a Sentry client that failed
 *   to initialise, say — would skip that update and leave the user staring at
 *   the silent, broken button this work exists to fix.
 */
const reportUnexpectedApiError = (args: ReportUnexpectedApiErrorArgs): void => {
  try {
    report(args);
  } catch (reportingError) {
    // Deliberately console, not Logger: Logger constructs a Splunk batch queue
    // at module scope, which is far too much to drag into every module that
    // reports an API error — and it starts network traffic of its own.
    // eslint-disable-next-line no-console
    console.warn(
      `Failed to report an unexpected error from ${args.operation}:`,
      reportingError instanceof Error ? reportingError.message : String(reportingError)
    );
  }
};

export default reportUnexpectedApiError;
